import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Layout, PrimaryButton, styleVariables, useLayoutContext } from '@lib/common';
import Icon from '@ant-design/icons';
import { useRouter } from 'next/router';
import HeaderContainer from '../../containers/test-maker-layout/header.container';
import ExploreIcon from '../../../public/icons/explore-icon';
import SpaceIcon from '../../../public/icons/space-icon';
import MyAssignmentIcon from '../../../public/icons/my-assignment-icon';
import dynamic from 'next/dynamic';
import { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Dropdown } from 'antd';
import { PlusOutlined, FolderOutlined, FileTextOutlined, CrownOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import {
  buildAuthenticationRedirectUrl,
  buildManageSubscriptionUrl,
  hasValidAccessToken,
  isPublicTestMakerBricksPath,
} from '../../utils/auth-guard.util';
import styled from 'styled-components';

const ExploreContainerDynamic = dynamic<{ selectedFolderId?: string }>(
  () => import('../../containers/explore/explore-popular-tests.container'),
  {
    ssr: false,
  },
);

const ListAssignmentContainerDynamic = dynamic(
  () => import('../../containers/assignment/list-assignment.container'),
);

const SpaceContainerDynamic = dynamic<{ selectedFolderId?: string }>(
  () => import('../../containers/space/space.container'),
  {
    ssr: false,
  },
);

const ListExaminationContainerDynamic = dynamic(
  () => import('../../containers/assignment/examination-list.container'),
);

const FormCreateModalDynamic = dynamic(
  () => import('../../containers/space/form/form-create.modal'),
  { ssr: false },
);
const FolderCreateModalDynamic = dynamic(
  () => import('../../containers/space/folder/folder-create.modal'),
  { ssr: false },
);

type SiderNewMenuItem = { key: string; label: string; icon?: React.ReactNode; onClick: () => void };

const StyledDropdownMenu = styled.div`
  width: 250px;
  border-radius: 8px;
  box-shadow:
    0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);
  border: 2px solid ${styleVariables.colors.gray9};
  background: #ffffff;
  overflow: hidden;

  .ant-dropdown-menu-item {
    padding: 8px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 500;
    color: #4a4a4a;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.04);
      color: #3366ff;
    }

    .anticon {
      font-size: 16px;
      color: inherit;
    }
  }
`;

const SiderNewDropdown = ({ menuItems }: { menuItems: SiderNewMenuItem[] }) => {
  const { collapsed } = useLayoutContext();
  const { t } = useTranslation('common');
  return (
    <Dropdown
      menu={{
        items: menuItems,
        className: 'styled-dropdown-menu',
      }}
      trigger={['click']}
      placement="bottomLeft"
      dropdownRender={(menu) => <StyledDropdownMenu>{menu}</StyledDropdownMenu>}
    >
      <PrimaryButton
        type="primary"
        icon={<PlusOutlined />}
        style={{
          width: '100%',
          ...(collapsed ? { justifyContent: 'center', padding: '0 8px' } : {}),
        }}
      >
        {!collapsed ? t('new') : undefined}
      </PrimaryButton>
    </Dropdown>
  );
};

const DashboardPage = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const router = useRouter();
  const isAuthenticated = hasValidAccessToken();
  const selectedTab = (router.query.menu as string) || (isAuthenticated ? 'space' : 'explore');
  /** Avoid rendering Space / Assignments before redirect when the URL still has `menu=space` etc. */
  const effectiveTab = isAuthenticated ? selectedTab : 'explore';
  const [isOpenSiderCreateModal, setIsOpenSiderCreateModal] = useState(false);
  const [isOpenFolderCreateModal, setIsOpenFolderCreateModal] = useState(false);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    if (!router.query.menu) {
      router.replace(
        {
          pathname: `/bricks`,
          query: { menu: isAuthenticated ? 'space' : 'explore', level: 1 },
        },
        undefined,
        { shallow: true },
      );
    }
  }, [router.isReady, router.query.menu, isAuthenticated]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    if (isAuthenticated) {
      return;
    }
    if (!isPublicTestMakerBricksPath(router.asPath)) {
      window.location.href = buildAuthenticationRedirectUrl(router.asPath);
    }
  }, [router.isReady, router.asPath, isAuthenticated]);

  const handleOpenCreateFormModal = useCallback(() => {
    setIsOpenSiderCreateModal(true);
  }, []);

  const handleOpenFolderCreateModal = useCallback(() => {
    setIsOpenFolderCreateModal(true);
  }, []);

  const handleTabChange = (menu: string) => {
    router.push(
      {
        pathname: `/bricks`,
        query: { menu, level: 1 },
      },
      undefined,
      { shallow: true },
    );
  };

  const menuItems = [
    {
      key: 'space',
      label: t('space'),
      icon: <Icon component={SpaceIcon} />,
      onClick: () => selectedTab !== 'space' && handleTabChange('space'),
    },
    {
      key: 'assignments',
      label: t('assignments'),
      icon: <Icon component={MyAssignmentIcon} />,
      children: [
        {
          key: 'practice',
          label: t('practice'),
          onClick: () => selectedTab !== 'practice' && handleTabChange('practice'),
        },
        {
          key: 'examination',
          label: t('examination'),
          onClick: () => selectedTab !== 'examination' && handleTabChange('examination'),
        },
      ],
    },
    {
      key: 'explore',
      label: t('explore'),
      icon: <Icon component={ExploreIcon} />,
      onClick: () => selectedTab !== 'explore' && handleTabChange('explore'),
    },
  ];

  const visibleMenuItems = isAuthenticated
    ? menuItems
    : menuItems.filter((item) => item.key === 'explore');

  const newMenuItems = [
    {
      key: 'create-folder',
      label: t('spaceNewFolder'),
      icon: <FolderOutlined />,
      onClick: handleOpenFolderCreateModal,
    },
    {
      key: 'create-form',
      label: t('spaceNewForm'),
      icon: <FileTextOutlined />,
      onClick: handleOpenCreateFormModal,
    },
  ];

  const handleNavigateToManageSubscription = useCallback(() => {
    window.open(buildManageSubscriptionUrl(), '_blank', 'noopener,noreferrer');
  }, []);

  const sidebarBottomItems = useMemo(
    () =>
      isAuthenticated
        ? [
            {
              key: 'manage-subscription',
              label: t('manageSubscription'),
              icon: <CrownOutlined />,
              onClick: handleNavigateToManageSubscription,
            },
          ]
        : [],
    [handleNavigateToManageSubscription, isAuthenticated, t],
  );

  const renderContent = useMemo(() => {
    switch (effectiveTab) {
      case 'explore':
        return <ExploreContainerDynamic />;
      case 'space':
        return <SpaceContainerDynamic />;
      case 'practice':
        return <ListAssignmentContainerDynamic />;
      case 'examination':
        return <ListExaminationContainerDynamic />;
      default:
        return null;
    }
  }, [effectiveTab]);

  return (
    <Layout>
      <Layout.Logo
        subTitle="Teacher Portal"
        logoSrc="/images/Favicon.svg"
        logoTitleSrc="/icons/digi-logo.svg"
        logoWidth={40}
        logoHeight={40}
        onLogoClick={() => handleTabChange('explore')}
      />
      <Layout.Header>
        <HeaderContainer />
      </Layout.Header>

      <Layout.Sider
        items={visibleMenuItems}
        bottomItems={sidebarBottomItems}
        defaultOpenKeys={isAuthenticated ? ['assignments'] : ['explore']}
        openKeys={isAuthenticated ? ['assignments'] : ['explore']}
        selectedKeys={[effectiveTab]}
        topContent={isAuthenticated ? <SiderNewDropdown menuItems={newMenuItems} /> : undefined}
        collapseLabel={t('collapseSidebar')}
        expandLabel={t('expandSidebar')}
      />
      <Layout.Content padding={24}>{renderContent}</Layout.Content>
      <FormCreateModalDynamic
        open={isOpenSiderCreateModal}
        onClose={() => setIsOpenSiderCreateModal(false)}
      />
      <FolderCreateModalDynamic
        open={isOpenFolderCreateModal}
        onClose={() => setIsOpenFolderCreateModal(false)}
      />
    </Layout>
  );
};
export const getStaticProps: GetStaticProps = async (context) => {
  const { locale } = context;

  if (locale === undefined) {
    throw new Error('locale is missing');
  }
  return {
    props: {
      ...(await serverSideTranslations(locale, ['explore', 'common', 'assignment', 'component', 'form'])),
    },
    revalidate: 300,
  };
};

DashboardPage.getLayout = function getLayout(page: ReactElement) {
  return page;
};

export default DashboardPage;
